import { ArrowRight } from 'lucide-react';
import { DateTime } from 'luxon';
import type React from 'react';
import { useIntl } from '@/core/i18n/use-intl';

import { ResolvedUser } from './activity-timeline';

/**
 * Header line for a single activity entry: the acting user followed by a
 * relative timestamp. Rendered once per activity so that entries which touch
 * multiple fields don't repeat the user/time on every change.
 */
export function ActivityHeader({
  userId,
  timestamp,
  children,
}: {
  userId?: string | null;
  timestamp: string;
  /** Optional inline action text (e.g. "created object"). */
  children?: React.ReactNode;
}) {
  const { locale } = useIntl();
  const dt = DateTime.fromISO(timestamp);
  const relative = dt.isValid ? dt.toRelative({ locale }) : null;
  const absolute = dt.isValid ? dt.toFormat('dd.MM.yyyy, HH:mm') : undefined;

  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
      <ResolvedUser userId={userId} />
      {children}
      {relative && (
        <span className="text-xs text-muted-foreground" title={absolute}>
          · {relative}
        </span>
      )}
    </div>
  );
}

/** Wrapper around the list of changes/notes that belong to one activity. */
export function ActivityBody({ children }: { children: React.ReactNode }) {
  return <div className="mt-1.5 flex flex-col gap-1.5">{children}</div>;
}

/**
 * A single field change rendered as a compact, self-contained block with an
 * aligned old → new value pair. Both values wrap gracefully so long content
 * (lists, contacts, descriptions) never breaks the layout.
 */
export function ActivityFieldChange({
  label,
  prefix,
  from,
  to,
}: {
  /** Resolved, human-readable field name. */
  label: React.ReactNode;
  /** Optional qualifier shown before the label (e.g. "Metadata field"). */
  prefix?: React.ReactNode;
  from: React.ReactNode;
  to: React.ReactNode;
}) {
  const { t } = useIntl();

  return (
    <div className="rounded-md border border-border/60 bg-muted/30 px-2.5 py-2">
      <div className="mb-1.5 flex flex-wrap items-baseline gap-x-1 text-[13px] leading-snug">
        {prefix && <span className="text-muted-foreground">{prefix}</span>}
        <span className="font-medium text-foreground">{label}</span>
      </div>
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-2 gap-y-1 text-[13px] leading-snug">
        <span className="select-none pt-px text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
          {t('activity.from')}
        </span>
        <span className="min-w-0 whitespace-pre-wrap break-words text-muted-foreground">
          {from}
        </span>
        <span className="select-none pt-px text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
          {t('activity.to')}
        </span>
        <span className="min-w-0 whitespace-pre-wrap break-words font-medium text-foreground">
          {to}
        </span>
      </div>
    </div>
  );
}

/**
 * A non-diff body line for events that aren't field changes (file added /
 * removed, etc.). Renders a muted action followed by an emphasized subject.
 */
export function ActivityNote({
  action,
  subject,
  from,
  to,
}: {
  action: React.ReactNode;
  subject?: React.ReactNode;
  /** Optional "from → to" pair (e.g. file renamed). */
  from?: React.ReactNode;
  to?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5 text-[13px] leading-snug">
      <span className="text-muted-foreground">{action}</span>
      {subject && (
        <span className="min-w-0 break-words font-medium text-foreground">{subject}</span>
      )}
      {(from || to) && (
        <span className="flex flex-wrap items-baseline gap-x-1.5">
          <span className="min-w-0 break-words text-muted-foreground">{from}</span>
          <ArrowRight className="size-3 shrink-0 text-muted-foreground/70" />
          <span className="min-w-0 break-words font-medium text-foreground">{to}</span>
        </span>
      )}
    </div>
  );
}
